import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Form, Input, Row, Col, Select, Button, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { 
  getProductTypes, 
  getProductUniverses,
  createProductType,
  createProductUniverse
} from '../../../core/api/products';
import { fetchBrandsBySelectedClients } from '../../../core/api/settings';

const { Option } = Select;

const ProductInfoSection = ({ form, product, uniqueId, clientId }) => {
  const [productTypes, setProductTypes] = useState([]);
  const [productUniverses, setProductUniverses] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [loadingUniverses, setLoadingUniverses] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newUniverseName, setNewUniverseName] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [addingUniverse, setAddingUniverse] = useState(false);
  
  // Brand selector states
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  
  // Function to fetch brands for a client
  const fetchBrands = useCallback(async (clientIds) => {
    if (!clientIds || clientIds.length === 0) return;
    
    setBrands([]);
    setSelectedBrand(null);
    setLoadingBrands(true);
    
    try {
      const response = await fetchBrandsBySelectedClients(clientIds);
      
      if (response && response.data) {
        setBrands(response.data);
        if (response.data.length === 1) {
          const autoSelectedBrand = response.data[0];
          setSelectedBrand(autoSelectedBrand);
          form.setFieldsValue({ brand: autoSelectedBrand.id });
        }
      }
    } catch (error) {
      message.error("Failed to fetch brands.");
    } finally {
      setLoadingBrands(false);
    }
  }, [form]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  useEffect(() => {
    if (product) {
      // Set selected brand if available
      if (product.brand) {
        setSelectedBrand(product.brand);
      }
      
      // Set product type and universe
      if (product.product_type) {
        form.setFieldsValue({ product_type: product.product_type });
      }
      
      if (product.product_universe) {
        form.setFieldsValue({ product_universe: product.product_universe });
      }
    }
  }, [product, form]);
  
  // Fetch brands when clientId changes
  useEffect(() => {
    if (clientId) {
      fetchBrands([clientId]);
    }
  }, [clientId, fetchBrands]);

  const fetchDropdownData = async () => {
    try {
      setLoadingTypes(true);
      setLoadingUniverses(true);
      
      const [typesResponse, universesResponse] = await Promise.all([
        getProductTypes(),
        getProductUniverses()
      ]);
      
      if (typesResponse.data) {
        setProductTypes(typesResponse.data);
      }
      
      if (universesResponse.data) {
        setProductUniverses(universesResponse.data);
      }
    } catch (error) {
      message.error('Failed to load dropdown data');
      console.error('Error fetching dropdown data:', error);
    } finally {
      setLoadingTypes(false);
      setLoadingUniverses(false);
    }
  };

  const handleAddProductType = async () => {
    if (!newTypeName.trim()) {
      message.error('Please enter a product type name');
      return;
    }
    
    try {
      setAddingType(true);
      const response = await createProductType({ name: newTypeName.trim() });
      if (response.data) {
        setProductTypes([...productTypes, response.data]);
        form.setFieldsValue({ product_type: response.data.id });
        setNewTypeName('');
        message.success('Product type added successfully');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to add product type');
      console.error('Error adding product type:', error);
    } finally {
      setAddingType(false);
    }
  };

  const handleAddProductUniverse = async () => {
    if (!newUniverseName.trim()) {
      message.error('Please enter a product universe name');
      return;
    }
    
    try {
      setAddingUniverse(true);
      const response = await createProductUniverse({ name: newUniverseName.trim() });
      if (response.data) {
        setProductUniverses([...productUniverses, response.data]);
        form.setFieldsValue({ product_universe: response.data.id });
        setNewUniverseName('');
        message.success('Product universe added successfully');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to add product universe');
      console.error('Error adding product universe:', error);
    } finally {
      setAddingUniverse(false);
    }
  };

  return (
    <>
      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            label="Product Name"
            name="name"
            rules={[{ required: true, message: 'Please enter product name' }]}
          >
            <Input placeholder="Enter product name" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="Product Code"
            name="product_code"
            rules={[{ required: true, message: 'Please enter product code' }]}
          >
            <Input placeholder="Enter product code" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            label="Internal Code"
            name="internal_code"
          >
            <Input placeholder="Enter internal code" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="Brand"
            name="brand"
            rules={[{ required: true, message: 'Please select a brand' }]}
          >
            <Select
              placeholder="Select Brand"
              options={brands.map((brand) => ({
                value: brand.id,
                label: brand.brand_name,
              }))}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={(value) => {
                const selectedBrandObj = brands.find(brand => brand.id === value);
                setSelectedBrand(selectedBrandObj);
                form.setFieldsValue({ brand: value });
              }}
              value={selectedBrand?.id}
              loading={loadingBrands}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Form.Item
            name="product_type"
            label="Product Type"
            rules={[{ required: true, message: 'Please select product type' }]}
          >
            <Select
              placeholder="Select product type"
              loading={loadingTypes}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {addingType ? (
                    <div style={{ padding: '8px' }}>
                      <Input
                        placeholder="Type name"
                        value={newTypeName}
                        onChange={(e) => setNewTypeName(e.target.value)}
                        style={{ marginBottom: '8px' }}
                      />
                      <Button
                        type="primary"
                        onClick={handleAddProductType}
                        loading={loadingTypes}
                        block
                      >
                        Add Type
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() => setAddingType(true)}
                      style={{ width: '100%', textAlign: 'left' }}
                    >
                      Add New Type
                    </Button>
                  )}
                </>
              )}
            >
              {productTypes.map((type) => (
                <Option key={type.id} value={type.id}>
                  {type.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="product_universe"
            label="Product Universe"
            rules={[{ required: true, message: 'Please select product universe' }]}
          >
            <Select
              placeholder="Select product universe"
              loading={loadingUniverses}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              dropdownRender={(menu) => (
                <>
                  {menu}
                  {addingUniverse ? (
                    <div style={{ padding: '8px' }}>
                      <Input
                        placeholder="Universe name"
                        value={newUniverseName}
                        onChange={(e) => setNewUniverseName(e.target.value)}
                        style={{ marginBottom: '8px' }}
                      />
                      <Button
                        type="primary"
                        onClick={handleAddProductUniverse}
                        loading={loadingUniverses}
                        block
                      >
                        Add Universe
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() => setAddingUniverse(true)}
                      style={{ width: '100%', textAlign: 'left' }}
                    >
                      Add New Universe
                    </Button>
                  )}
                </>
              )}
            >
              {productUniverses.map((universe) => (
                <Option key={universe.id} value={universe.id}>
                  {universe.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};

export default ProductInfoSection;
